import data from '../../../data/cyclesData.js';
import { weekdayStartPeriod, getDailyCyclePeriod, getSlotForCycle, getCycleForSlot, dateOnWeekday, formatPeriodRange } from './cyclePeriods.js';

const cycleAngle = 360 / 7;
const msPerDay = 86400 * 1000;

var globals = {
  currentCycle: 0,
  timeMarkerAngle: 0,
  cyclesViewBox: null,
  animationIsRunning: false,
  dialAnimationActive: false,
  markerAnimationActive: false,
  browseMode: false,
  browseDate: null,
  pendingRotationRequest: null,
  wheelUpdateTimer: null,

  inspect: function () {
    return { ...this };
  },
};

var elements = {
  cyclesOfLifeWheel: null,
  dailyCycles: null,
  timeMarker: null,
  timeElements: null,
  weekdays: null,

  planets: null,

  title: null,
  cycleRange: null,
  periodTime: null,
  summary: null,
  description: null,
  btnPrevCycle: null,
  btnNextCycle: null,
  btnNow: null,

  tabsContainer: null,
  panelsContainer: null,
  panels: null,
};

var cyclesData = data();

function rotateElement(element, viewBox, angle) {
  const makeProp = (property, array) => property + '(' + array.join(' ') + ')';
  const translateX = 0;
  const translateY = 0;
  const centerX = viewBox[0] + (viewBox[2] - viewBox[0]) / 2;
  const centerY = viewBox[1] + (viewBox[3] - viewBox[1]) / 2;
  let trans = [makeProp('translate', [translateX, translateY]), makeProp('rotate', [angle, centerX, centerY])];
  element.setAttribute('transform', trans);
}

function easeInOutQuad(x) {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}

function normalizeAngle(angle) {
  if (angle < 0) {
    return angle + 360;
  } else if (angle >= 360) {
    return angle - 360;
  }
  return angle;
}

function rotateElementToNewAngle(elementsToRotate, viewBox, startAngle, endAngle, clockwise = true) {
  if (startAngle === endAngle) return;

  globals.animationIsRunning = true;
  const range = normalizeAngle(endAngle - startAngle);
  const frameRate = 1000 / 100;
  const duration = (range / cycleAngle) * 10;
  const direction = clockwise ? 1 : -1;
  const increment = range / (frameRate * duration);

  let currentAngle = 0;
  let timerToken = setInterval(() => {
    let pendingRotaion = null;
    currentAngle += increment;
    if (currentAngle >= range) {
      currentAngle = range;
      clearInterval(timerToken);
      timerToken = null;
      globals.animationIsRunning = false;
      pendingRotaion = globals.pendingRotationRequest;
      globals.pendingRotationRequest = null;
    }
    const progress = 1 - Math.abs((currentAngle - range) / range);
    const easedValue = easeInOutQuad(progress);

    if (elementsToRotate.constructor == Array) {
      elementsToRotate.forEach((element) => rotateElement(element, viewBox, normalizeAngle((startAngle + range * easedValue) * direction)));
    } else {
      rotateElement(elementsToRotate, viewBox, normalizeAngle((startAngle + range * easedValue) * direction));
    }

    if (pendingRotaion != null) {
      rotateDialToCycle(pendingRotaion);
    }
  }, frameRate);
}

function rotateDialToCycle(newCycle) {
  if (!globals.animationIsRunning) {
    const currentAngle = globals.currentCycle * cycleAngle;
    const newAngle = newCycle * cycleAngle;
    rotateElementToNewAngle([elements.dailyCycles, elements.weekdays], globals.cyclesViewBox, currentAngle, newAngle, false);
    globals.currentCycle = newCycle % 7;
  } else {
    globals.pendingRotationRequest = newCycle;
  }
}

function rotateDialToWeekdayStartPeriod(time) {
  const startOfDayPeriod = weekdayStartPeriod[time.getDay()];
  rotateDialToCycle(startOfDayPeriod);
}

function getTimeMarkerAngle() {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  return ((now.getTime() - startOfDay.getTime()) / msPerDay) * 360;
}

function setCurrentTimeMarker() {
  const timeAngle = getTimeMarkerAngle();
  if (Math.abs(timeAngle - globals.timeMarkerAngle) < 5) {
    rotateElement(elements.timeMarker, globals.cyclesViewBox, timeAngle);
  } else {
    rotateElementToNewAngle(elements.timeMarker, globals.cyclesViewBox, globals.timeMarkerAngle, timeAngle);
  }
  globals.timeMarkerAngle = timeAngle;
}

// Renders copy as one <p> per blank-line-separated paragraph. innerText's
// <br><br> pairs spaced paragraphs inconsistently across browsers (they ran
// together in Firefox). Single newlines inside a paragraph are kept by the
// container's whitespace-pre-line.
function setParagraphs(element, text) {
  const paragraphs = text.split(/\n\s*\n/).map((paragraph) => paragraph.trim()).filter(Boolean);
  element.replaceChildren(
    ...paragraphs.map((paragraph) => {
      const p = document.createElement('p');
      p.textContent = paragraph;
      return p;
    })
  );
}

// Shows a cycle's text plus the day and time slot that cycle occupies on
// `date`'s weekday. In browse mode the arrows step through letters, so the
// range shown is where that letter falls on the browsed day (today, unless a
// Weekly Overview letter picked another weekday).
function displayCycleData(cycle, date = new Date()) {
  const cycleData = cyclesData.dailyCycle[cycle];
  if (elements.title) elements.title.innerText = 'Cycle: ' + cycleData.cycle;
  if (elements.cycleRange) elements.cycleRange.innerText = 'Day: ' + cyclesData.weekdays[date.getDay()];
  if (elements.periodTime) elements.periodTime.innerText = formatPeriodRange(date, getSlotForCycle(cycle, date.getDay()));
  if (elements.summary) setParagraphs(elements.summary, cycleData.summary);
  if (elements.description) setParagraphs(elements.description, cycleData.full_description);
  if (elements.planets) {
    elements.planets.forEach((planet) => planet.setAttribute('visibility', 'hidden'));
    elements.planets[cycle].setAttribute('visibility', 'visible');
  }
}

function setPeriodData(time) {
  displayCycleData(getDailyCyclePeriod(time), time);
}

function displayCycleNow() {
  setCurrentTimeMarker();
  setPeriodData(new Date());
}

// Wheel-only update - just moves time marker
function updateWheelOnly() {
  setCurrentTimeMarker();
}

function updateFull() {
  const updateInterval = 20 * 1000;
  let timerToken = setInterval(() => {
    if (globals.browseMode) {
      clearInterval(timerToken);
      timerToken = null;
    } else {
      displayCycleNow();
    }
  }, updateInterval);
}

// Underlines the tab whose data-target is `target` and shows its panel.
function activateTab(target) {
  // Deactivate all tabs
  elements.tabsContainer.forEach((tab) => tab.children[0].classList.remove('border-lz-ember', 'dark:border-lz-candle-dim', 'border-b-4', 'md:border-b-0'));

  // Hide all panels
  elements.panels.forEach((panel) => panel.classList.add('hidden'));

  // Activate new tab and panel
  const tab = Array.from(elements.tabsContainer).find((t) => t.getAttribute('data-target') === target);
  tab.children[0].classList.add('border-lz-ember', 'dark:border-lz-candle-dim', 'border-b-4');
  elements.panelsContainer.getElementsByClassName(target)[0].classList.remove('hidden');
}

function onTabClick(event) {
  const classString = event.currentTarget.getAttribute('data-target');
  activateTab(classString);
  elements.timeElements?.setAttribute('opacity', '1');
  switch (classString) {
    case 'panel-1':
      globals.browseDate = null;
      elements.timeMarker?.setAttribute('visibility', 'visible');
      rotateDialToWeekdayStartPeriod(new Date());
      displayCycleNow();
      break;

    case 'panel-2':
      elements.timeMarker?.setAttribute('visibility', 'hidden');
      break;

    case 'panel-3':
      elements.timeMarker?.setAttribute('visibility', 'visible');
      break;
  }
}

// Weekly Overview letter click: opens the Cycle Description tab on the cycle
// in `slot` of `weekday`, browsing it the way the arrow buttons do.
export function showWeeklyOverviewCycle(weekday, slot) {
  if (!elements.panelsContainer) return;

  const cycle = getCycleForSlot(weekday, slot);
  globals.browseMode = true;
  globals.browseDate = dateOnWeekday(new Date(), weekday);
  activateTab('panel-1');
  elements.timeMarker?.setAttribute('visibility', 'hidden');
  elements.timeElements?.setAttribute('opacity', '.3');
  rotateDialToCycle(cycle);
  displayCycleData(cycle, globals.browseDate);
}

function startWheelWhenLoaded() {
  if (elements.dailyCycles) {
    setCurrentTimeMarker();
    // Update wheel every 20 seconds
    if (globals.wheelUpdateTimer) clearInterval(globals.wheelUpdateTimer);
    globals.wheelUpdateTimer = setInterval(() => {
      updateWheelOnly();
    }, 20 * 1000);
  }
}

function startFullWhenLoaded() {
  if (elements.planets && elements.dailyCycles) {
    displayCycleNow();
    updateFull();
  }
}

// Initialize just the wheel animation (for collapsed view)
export function initializeWheel(svgObjectId = 'cyclesOfLife') {
  const now = new Date();

  const svgObject = document.getElementById(svgObjectId);
  if (!svgObject) return;

  // Check if already loaded
  if (svgObject.contentDocument && svgObject.contentDocument.getElementById('dailyCycles')) {
    const svgDoc = svgObject.contentDocument;
    const viewBox = svgDoc.documentElement.getAttribute('viewBox');
    globals.cyclesViewBox = viewBox.split(/\s+|,/);
    elements.dailyCycles = svgDoc.getElementById('dailyCycles');
    elements.timeMarker = svgDoc.getElementById('timeMarker');
    elements.timeElements = svgDoc.getElementById('timeElements');
    elements.weekdays = svgDoc.getElementById('weekdays');

    globals.currentCycle = 0;
    rotateDialToWeekdayStartPeriod(now);
    globals.timeMarkerAngle = getTimeMarkerAngle() - cycleAngle;
    startWheelWhenLoaded();
  } else {
    // Wait for load
    svgObject.addEventListener(
      'load',
      (event) => {
        const svgDoc = event.target.contentDocument;
        const viewBox = svgDoc.documentElement.getAttribute('viewBox');
        globals.cyclesViewBox = viewBox.split(/\s+|,/);
        elements.dailyCycles = svgDoc.getElementById('dailyCycles');
        elements.timeMarker = svgDoc.getElementById('timeMarker');
        elements.timeElements = svgDoc.getElementById('timeElements');
        elements.weekdays = svgDoc.getElementById('weekdays');

        globals.currentCycle = 0;
        rotateDialToWeekdayStartPeriod(now);
        globals.timeMarkerAngle = getTimeMarkerAngle() - cycleAngle;
        startWheelWhenLoaded();
      },
      false
    );
  }
}

// Initialize full UI (for expanded modal view)
export function initializeUI() {
  const now = new Date();

  // Each open starts on the live cycle, even if the last one ended browsing
  globals.browseMode = false;
  globals.browseDate = null;

  // Text elements
  elements.cycleRange = document.getElementById('cycleRange');
  elements.periodTime = document.getElementById('periodTime');
  elements.title = document.getElementById('periodTitle');
  elements.summary = document.getElementById('periodSummary');
  elements.description = document.getElementById('periodDescription');

  // Buttons
  elements.btnPrevCycle = document.getElementById('btnPrevCycle');
  if (elements.btnPrevCycle) {
    elements.btnPrevCycle.addEventListener(
      'mousedown',
      () => {
        globals.browseMode = true;
        elements.timeMarker?.setAttribute('visibility', 'hidden');
        elements.timeElements?.setAttribute('opacity', '.3');
        const newCycle = (globals.currentCycle - 1 + 7) % 7;
        rotateDialToCycle(newCycle);
        displayCycleData(newCycle, globals.browseDate || new Date());
      },
      false
    );
  }

  elements.btnNextCycle = document.getElementById('btnNextCycle');
  if (elements.btnNextCycle) {
    elements.btnNextCycle.addEventListener(
      'mousedown',
      () => {
        globals.browseMode = true;
        elements.timeMarker?.setAttribute('visibility', 'hidden');
        elements.timeElements?.setAttribute('opacity', '.3');
        const newCycle = (globals.currentCycle + 1) % 7;
        rotateDialToCycle(newCycle);
        displayCycleData(newCycle, globals.browseDate || new Date());
      },
      false
    );
  }

  elements.btnNow = document.getElementById('btnNow');
  if (elements.btnNow) {
    elements.btnNow.addEventListener(
      'mousedown',
      () => {
        globals.browseMode = false;
        globals.browseDate = null;
        elements.timeMarker?.setAttribute('visibility', 'visible');
        elements.timeElements?.setAttribute('opacity', '1');
        displayCycleNow();
        const startOfDayPeriod = weekdayStartPeriod[now.getDay()];
        rotateDialToCycle(startOfDayPeriod);
      },
      false
    );
  }

  // Weekday buttons
  for (let i = 0; i < 7; i++) {
    const btn = document.getElementById('btn-weekday-' + i);
    if (btn) btn.addEventListener('mousedown', () => rotateDialToCycle(weekdayStartPeriod[i]));
  }
  for (let i = 0; i < 7; i++) {
    const btn = document.getElementById('btn-weekday-1' + i);
    if (btn) btn.addEventListener('mousedown', () => rotateDialToCycle(weekdayStartPeriod[i]));
  }

  // Planet Symbols
  let planets = document.getElementById('planets');
  if (planets) {
    const setupPlanets = (svgDoc) => {
      elements.planets = [];
      elements.planets.push(svgDoc.getElementById('sun'));
      elements.planets.push(svgDoc.getElementById('venus'));
      elements.planets.push(svgDoc.getElementById('mercury'));
      elements.planets.push(svgDoc.getElementById('moon'));
      elements.planets.push(svgDoc.getElementById('saturn'));
      elements.planets.push(svgDoc.getElementById('jupiter'));
      elements.planets.push(svgDoc.getElementById('mars'));
      elements.planets.forEach((planet) => planet && planet.setAttribute('visibility', 'hidden'));
      startFullWhenLoaded();
    };

    if (planets.contentDocument && planets.contentDocument.getElementById('sun')) {
      setupPlanets(planets.contentDocument);
    } else {
      planets.addEventListener('load', (event) => {
        setupPlanets(event.target.contentDocument);
      });
    }
  }

  // Main wheel SVG
  const cyclesWheel = document.getElementById('cyclesOfLifeModal');
  if (cyclesWheel) {
    const setupWheel = (svgDoc) => {
      const viewBox = svgDoc.documentElement.getAttribute('viewBox');
      globals.cyclesViewBox = viewBox.split(/\s+|,/);
      elements.dailyCycles = svgDoc.getElementById('dailyCycles');
      elements.timeMarker = svgDoc.getElementById('timeMarker');
      elements.timeElements = svgDoc.getElementById('timeElements');
      elements.weekdays = svgDoc.getElementById('weekdays');

      // Reset currentCycle since modal SVG starts at 0 degrees
      globals.currentCycle = 0;
      rotateDialToWeekdayStartPeriod(now);
      globals.timeMarkerAngle = getTimeMarkerAngle() - cycleAngle;
      startFullWhenLoaded();
    };

    if (cyclesWheel.contentDocument && cyclesWheel.contentDocument.getElementById('dailyCycles')) {
      setupWheel(cyclesWheel.contentDocument);
    } else {
      cyclesWheel.addEventListener('load', (event) => {
        setupWheel(event.target.contentDocument);
      }, false);
    }
  }

  elements.tabsContainer = document.querySelectorAll('.cycles-tab');
  elements.panelsContainer = document.getElementById('panelsContainer');
  elements.panels = document.querySelectorAll('.cycles-panel');
  elements.tabsContainer.forEach((tab) => tab.addEventListener('click', onTabClick));
}

// Clean up timers when component unmounts
export function cleanup() {
  if (globals.wheelUpdateTimer) {
    clearInterval(globals.wheelUpdateTimer);
    globals.wheelUpdateTimer = null;
  }
}
